import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { 
  ArrowLeft, Building2, FileText, AlertTriangle, Shield, Users, 
  TrendingUp, TrendingDown, Activity, RefreshCw, Target, Award,
  AlertCircle, CheckCircle, Clock, Zap, Brain, ChevronRight,
  BarChart3, PieChart, Lightbulb, Flame, Minus, Info
} from 'lucide-react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';

// ============================================
// INFO TOOLTIP COMPONENT
// ============================================

const InfoTooltip = ({ explanation }) => {
  if (!explanation) return null;
  
  return (
    <TooltipProvider>
      <Tooltip delayDuration={200}>
        <TooltipTrigger asChild>
          <button className="ml-1.5 text-gray-400 hover:text-gray-600 transition-colors inline-flex items-center">
            <Info className="w-4 h-4" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-sm p-3 bg-white border shadow-lg rounded-lg">
          <div className="space-y-2">
            <p className="font-semibold text-gray-900 text-sm">{explanation.title}</p>
            <p className="text-xs text-gray-600">{explanation.summary}</p>
            {explanation.formula && (
              <p className="text-xs font-mono bg-gray-50 p-1.5 rounded text-gray-700">{explanation.formula}</p>
            )}
            {explanation.details && (
              <ul className="text-xs text-gray-600 space-y-0.5 mt-1">
                {explanation.details.slice(0, 4).map((detail, i) => (
                  <li key={i} className="flex items-start gap-1">
                    <span className="text-blue-500 mt-0.5">•</span>
                    <span>{detail}</span>
                  </li>
                ))}
              </ul>
            )}
            {explanation.severityTable && (
              <div className="mt-2 border-t pt-2">
                <p className="text-xs font-medium text-gray-700 mb-1">Severity Deductions:</p>
                <div className="grid grid-cols-2 gap-x-2 gap-y-0.5 text-xs">
                  {Object.entries(explanation.severityTable).slice(0, 4).map(([key, val]) => (
                    <div key={key} className="flex justify-between">
                      <span className="text-gray-600">{key}:</span>
                      <span className="font-medium text-red-600">{val}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {explanation.riskBands && (
              <div className="mt-2 border-t pt-2">
                <p className="text-xs font-medium text-gray-700 mb-1">Risk Levels:</p>
                <div className="space-y-0.5 text-xs">
                  {Object.entries(explanation.riskBands).slice(0, 3).map(([range, label]) => (
                    <div key={range} className="flex justify-between">
                      <span className="text-gray-600">{range}:</span>
                      <span className="font-medium">{label}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {explanation.levels && (
              <div className="mt-2 border-t pt-2">
                <p className="text-xs font-medium text-gray-700 mb-1">Risk Levels:</p>
                <div className="space-y-0.5 text-xs">
                  {Object.entries(explanation.levels).slice(0, 3).map(([level, desc]) => (
                    <div key={level} className="flex items-start gap-1">
                      <span className="font-medium text-gray-800 whitespace-nowrap">{level}:</span>
                      <span className="text-gray-600">{desc}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {explanation.philosophy && (
              <p className="text-xs italic text-gray-500 border-t pt-2 mt-2">{explanation.philosophy}</p>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// ============================================
// HELPER COMPONENTS
// ============================================

// Circular Progress Ring for Safety Score
const SafetyScoreRing = ({ score, status, color, size = 'large' }) => {
  const radius = size === 'large' ? 70 : 45;
  const strokeWidth = size === 'large' ? 10 : 6;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  
  const colorMap = {
    green: { stroke: '#22c55e', bg: 'bg-green-50', text: 'text-green-600' },
    yellow: { stroke: '#eab308', bg: 'bg-yellow-50', text: 'text-yellow-600' },
    red: { stroke: '#ef4444', bg: 'bg-red-50', text: 'text-red-600' },
    gray: { stroke: '#9ca3af', bg: 'bg-gray-50', text: 'text-gray-600' }
  };
  
  const colors = colorMap[color] || colorMap.gray;
  const viewBoxSize = (radius + strokeWidth) * 2;
  const center = radius + strokeWidth;
  
  return (
    <div className={`relative inline-flex items-center justify-center ${colors.bg} rounded-full p-2`}>
      <svg 
        width={viewBoxSize} 
        height={viewBoxSize} 
        className="transform -rotate-90"
      >
        {/* Background circle */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth={strokeWidth}
        />
        {/* Progress circle */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={colors.stroke}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className="transition-all duration-1000 ease-out"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={`${size === 'large' ? 'text-3xl' : 'text-xl'} font-bold ${colors.text}`}>
          {score}
        </span>
        <span className={`${size === 'large' ? 'text-sm' : 'text-xs'} text-gray-500`}>
          {status}
        </span>
      </div>
    </div>
  );
};

// Risk Badge Component
const RiskBadge = ({ level }) => {
  const config = {
    Low: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle },
    Medium: { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: AlertCircle },
    High: { bg: 'bg-orange-100', text: 'text-orange-700', icon: AlertTriangle },
    Critical: { bg: 'bg-red-100', text: 'text-red-700', icon: Flame }
  };
  
  const c = config[level] || config.Medium;
  const Icon = c.icon;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${c.bg} ${c.text}`}>
      <Icon className="w-3 h-3" />
      {level}
    </span>
  );
};

// Trend Indicator
const TrendIndicator = ({ direction, value }) => {
  if (direction === 'increasing' || value > 0) {
    return (
      <span className="inline-flex items-center gap-1 text-red-600 text-sm">
        <TrendingUp className="w-4 h-4" />
        {value ? `+${Math.abs(value)}%` : 'Rising'}
      </span>
    );
  }
  if (direction === 'decreasing' || value < 0) {
    return (
      <span className="inline-flex items-center gap-1 text-green-600 text-sm">
        <TrendingDown className="w-4 h-4" />
        {value ? `-${Math.abs(value)}%` : 'Falling'}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-gray-500 text-sm">
      <Minus className="w-4 h-4" />
      Stable
    </span>
  );
};

// Alert Card
const AlertCard = ({ alert }) => {
  const severityConfig = {
    high: { bg: 'bg-red-50', border: 'border-red-200', icon: 'text-red-500' },
    medium: { bg: 'bg-yellow-50', border: 'border-yellow-200', icon: 'text-yellow-500' },
    low: { bg: 'bg-blue-50', border: 'border-blue-200', icon: 'text-blue-500' }
  };
  
  const c = severityConfig[alert.severity] || severityConfig.medium;
  
  return (
    <div className={`p-3 rounded-lg border ${c.bg} ${c.border}`}>
      <div className="flex items-start gap-2">
        <AlertCircle className={`w-4 h-4 mt-0.5 ${c.icon}`} />
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-800">{alert.subCompanyName}</p>
          <p className="text-xs text-gray-600 mt-0.5">{alert.message}</p>
        </div>
      </div>
    </div>
  );
};

// Stat Card
const StatCard = ({ icon: Icon, label, value, subValue, color = 'blue', onClick }) => {
  const colorMap = {
    blue: 'bg-blue-50 text-blue-600',
    red: 'bg-red-50 text-red-600',
    green: 'bg-green-50 text-green-600',
    yellow: 'bg-yellow-50 text-yellow-600',
    purple: 'bg-purple-50 text-purple-600',
    orange: 'bg-orange-50 text-orange-600'
  };
  
  return (
    <div 
      className={`p-4 rounded-xl border border-gray-100 bg-white shadow-sm ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
      onClick={onClick}
    >
      <div className={`w-10 h-10 rounded-lg ${colorMap[color]} flex items-center justify-center mb-3`}>
        <Icon className="w-5 h-5" />
      </div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      <p className="text-sm text-gray-500">{label}</p>
      {subValue && <p className="text-xs text-gray-400 mt-1">{subValue}</p>}
    </div>
  );
};

// ============================================
// MAIN DASHBOARD COMPONENT
// ============================================

const GCAnalyticsDashboard = ({ user }) => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [aiInsights, setAiInsights] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(projectId || null);
  const [explanations, setExplanations] = useState(null);
  
  useEffect(() => {
    fetchProjects();
    fetchExplanations();
  }, []);
  
  useEffect(() => {
    if (selectedProjectId) {
      fetchDashboardData();
    }
  }, [selectedProjectId]);
  
  const fetchProjects = async () => {
    try {
      const response = await axiosInstance.get('/gc/projects');
      setProjects(response.data);
      if (!selectedProjectId && response.data.length > 0) {
        setSelectedProjectId(response.data[0].id);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };
  
  const fetchExplanations = async () => {
    try {
      const response = await axiosInstance.get('/gc/analytics/explanations');
      setExplanations(response.data);
    } catch (error) {
      console.error('Error fetching explanations:', error);
    }
  };
  
  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`/gc/projects/${selectedProjectId}/analytics-dashboard`);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      toast.error('Failed to load analytics dashboard');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchAiInsights = async () => {
    setAiLoading(true);
    try {
      const response = await axiosInstance.get(`/gc/projects/${selectedProjectId}/ai-insights`);
      setAiInsights(response.data);
    } catch (error) {
      console.error('Error fetching AI insights:', error);
      toast.error('Failed to generate AI insights');
    } finally {
      setAiLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-500">Loading analytics...</p>
        </div>
      </div>
    );
  }
  
  if (!data) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto text-center py-12">
          <BarChart3 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-600">No Analytics Data Available</h2>
          <Button className="mt-4" onClick={() => navigate('/gc/dashboard')}>
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }
  
  const { portfolioRisk, compliance, indicators, subcontractorScorecard, executiveKpis } = data;
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/gc/dashboard')}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Analytics Dashboard</h1>
                <p className="text-sm text-gray-500">{data.projectName}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Select value={selectedProjectId} onValueChange={setSelectedProjectId}>
                <SelectTrigger className="w-64">
                  <SelectValue placeholder="Select project" />
                </SelectTrigger>
                <SelectContent>
                  {projects.map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4" />
                        {p.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Button variant="outline" size="sm" onClick={fetchDashboardData}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* LAYER 1: Portfolio-Level Risk */}
        <section>
          <div className="flex items-center gap-2 mb-4">
            <Target className="w-5 h-5 text-indigo-600" />
            <h2 className="text-lg font-semibold text-gray-900">Portfolio-Level Risk</h2>
          </div>
          
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Overall Safety Health Score */}
            <Card className="lg:col-span-1">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center">
                  Overall Safety Health Score
                  <InfoTooltip explanation={explanations?.safetyHealthScore} />
                </CardTitle>
                <CardDescription>Weighted average across all metrics</CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col items-center pt-4">
                <SafetyScoreRing 
                  score={portfolioRisk?.overallHealthScore?.score || 0}
                  status={portfolioRisk?.overallHealthScore?.status || 'No Data'}
                  color={portfolioRisk?.overallHealthScore?.color || 'gray'}
                  size="large"
                />
                
                {/* Score Breakdown */}
                {portfolioRisk?.overallHealthScore?.breakdown && (
                  <div className="w-full mt-6 space-y-3">
                    {Object.entries(portfolioRisk.overallHealthScore.breakdown).map(([key, val]) => (
                      <div key={key} className="flex items-center justify-between text-sm">
                        <span className="text-gray-600 capitalize flex items-center">
                          {key.replace(/([A-Z])/g, ' $1').trim()}
                          {key === 'incidentScore' && <InfoTooltip explanation={explanations?.incidentScore} />}
                        </span>
                        <div className="flex items-center gap-2">
                          <Progress value={val.value} className="w-20 h-2" />
                          <span className="font-medium w-12 text-right">{val.value}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Risk Distribution */}
            <Card className="lg:col-span-2">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center">
                  Project Risk Distribution
                  <InfoTooltip explanation={explanations?.riskDistribution} />
                </CardTitle>
                <CardDescription>Subcontractors by risk level</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-4 gap-4 mb-6">
                  {['low', 'medium', 'high', 'critical'].map(level => {
                    const count = portfolioRisk?.riskDistribution?.counts?.[level] || 0;
                    const pct = portfolioRisk?.riskDistribution?.percentages?.[level] || 0;
                    const colorMap = {
                      low: 'bg-green-100 text-green-700 border-green-200',
                      medium: 'bg-yellow-100 text-yellow-700 border-yellow-200',
                      high: 'bg-orange-100 text-orange-700 border-orange-200',
                      critical: 'bg-red-100 text-red-700 border-red-200'
                    };
                    
                    return (
                      <div key={level} className={`p-4 rounded-xl border ${colorMap[level]}`}>
                        <p className="text-3xl font-bold">{count}</p>
                        <p className="text-sm capitalize">{level} Risk</p>
                        <p className="text-xs opacity-75">{pct}%</p>
                      </div>
                    );
                  })}
                </div>
                
                {/* Subcontractor List by Score */}
                {portfolioRisk?.subcontractorScores?.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-gray-700 mb-2">Subcontractors by Score</p>
                    {portfolioRisk.subcontractorScores.map(sub => (
                      <div key={sub.subCompanyId} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-gray-400" />
                          <span className="text-sm font-medium">{sub.subCompanyName}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-bold">{sub.score}</span>
                          <RiskBadge level={sub.risk} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </section>
        
        {/* LAYER 2: Compliance & Readiness */}
        <section>
          <div className="flex items-center gap-2 mb-4">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <h2 className="text-lg font-semibold text-gray-900">Compliance & Readiness</h2>
          </div>
          
          {/* JSA Compliance - Full Width */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="w-4 h-4 text-blue-600" />
                JSA Compliance
                <InfoTooltip explanation={explanations?.jsaCompliance} />
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-4xl font-bold text-blue-600">
                    {compliance?.jsaCompliance?.overallRate || 0}%
                  </p>
                  <p className="text-sm text-gray-500">
                    {compliance?.jsaCompliance?.completedJsas || 0} of {compliance?.jsaCompliance?.totalJsas || 0} completed
                  </p>
                </div>
                <div className="text-right text-sm">
                  {Object.entries(compliance?.jsaCompliance?.byStatus || {}).map(([status, count]) => (
                    <div key={status} className="flex items-center justify-end gap-2">
                      <span className="text-gray-500 capitalize">{status}:</span>
                      <span className="font-medium">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <Progress value={compliance?.jsaCompliance?.overallRate || 0} className="h-3 mb-4" />
              
              {/* By Subcontractor */}
              {compliance?.jsaCompliance?.bySubcontractor?.length > 0 && (
                <div className="space-y-2 mt-4 pt-4 border-t">
                  <p className="text-xs font-medium text-gray-500 uppercase">By Subcontractor</p>
                  {compliance.jsaCompliance.bySubcontractor.map(sub => (
                    <div key={sub.subCompanyId} className="flex items-center justify-between text-sm">
                      <span className="text-gray-600 truncate max-w-[200px]">{sub.subCompanyName}</span>
                      <div className="flex items-center gap-2">
                        <Progress value={sub.complianceRate} className="w-16 h-2" />
                        <span className="font-medium w-12 text-right">{sub.complianceRate}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </section>
        
        {/* LAYER 3: Leading vs Lagging Indicators */}
        <section>
          <div className="flex items-center gap-2 mb-4">
            <Activity className="w-5 h-5 text-purple-600" />
            <h2 className="text-lg font-semibold text-gray-900">Leading vs Lagging Indicators</h2>
          </div>
          
          <Tabs defaultValue="leading" className="w-full">
            <TabsList className="mb-4">
              <TabsTrigger value="leading" className="flex items-center gap-2">
                <Lightbulb className="w-4 h-4" />
                Leading (Predictive)
              </TabsTrigger>
              <TabsTrigger value="lagging" className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Lagging (Traditional)
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="leading">
              <div className="grid md:grid-cols-3 gap-6">
                {/* Near Miss Trend */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Near-Miss Trend</CardTitle>
                    <CardDescription>Last 3 months</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between mb-4">
                      <p className="text-3xl font-bold">{indicators?.leading?.nearMisses?.total || 0}</p>
                      <TrendIndicator direction={indicators?.leading?.nearMisses?.trendDirection} />
                    </div>
                    
                    {indicators?.leading?.nearMisses?.alert && (
                      <div className="p-2 bg-red-50 rounded-lg text-red-700 text-sm mb-4">
                        ⚠️ Near-misses are increasing
                      </div>
                    )}
                    
                    <div className="space-y-2">
                      {indicators?.leading?.nearMisses?.trend?.map((month, idx) => (
                        <div key={idx} className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">{month.month}</span>
                          <div className="flex items-center gap-2">
                            <div className="w-24 bg-gray-100 rounded-full h-2">
                              <div 
                                className="bg-purple-500 h-2 rounded-full" 
                                style={{ width: `${Math.min(month.count * 20, 100)}%` }}
                              />
                            </div>
                            <span className="font-medium w-6 text-right">{month.count}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
                
                {/* Top Hazard Patterns */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Hazard Patterns</CardTitle>
                    <CardDescription>Most common hazards identified</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {indicators?.leading?.hazardPatterns?.slice(0, 5).map((hazard, idx) => (
                        <div key={idx} className="p-2 bg-gray-50 rounded-lg">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-800">{hazard.hazard}</span>
                            <Badge variant="secondary">{hazard.occurrences}x</Badge>
                          </div>
                          {hazard.commonTasks?.length > 0 && (
                            <p className="text-xs text-gray-500 mt-1 truncate">
                              Tasks: {hazard.commonTasks.join(', ')}
                            </p>
                          )}
                        </div>
                      ))}
                      
                      {(!indicators?.leading?.hazardPatterns || indicators.leading.hazardPatterns.length === 0) && (
                        <p className="text-sm text-gray-400 italic text-center py-4">No hazard patterns detected</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
                
                {/* Audit Score Trend */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Audit Score Trend</CardTitle>
                    <CardDescription>Average scores over time</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {indicators?.leading?.auditTrend?.map((month, idx) => (
                        <div key={idx} className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">{month.month}</span>
                          <div className="flex items-center gap-2">
                            {month.avgScore !== null ? (
                              <>
                                <Progress value={month.avgScore} className="w-20 h-2" />
                                <span className="font-medium text-sm">{month.avgScore}%</span>
                              </>
                            ) : (
                              <span className="text-gray-400 text-sm">No audits</span>
                            )}
                            <Badge variant="outline" className="text-xs">{month.count}</Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
            
            <TabsContent value="lagging">
              <div className="grid md:grid-cols-4 gap-4 mb-6">
                <StatCard 
                  icon={AlertTriangle} 
                  label="Total Incidents" 
                  value={indicators?.lagging?.totalIncidents || 0}
                  color="red"
                />
                <StatCard 
                  icon={Flame} 
                  label="Recordable" 
                  value={indicators?.lagging?.recordableIncidents || 0}
                  color="orange"
                />
                <StatCard 
                  icon={Activity} 
                  label="First Aid Cases" 
                  value={indicators?.lagging?.firstAidCases || 0}
                  color="yellow"
                />
                <StatCard 
                  icon={CheckCircle} 
                  label="Days Since Last Incident" 
                  value={indicators?.lagging?.daysSinceLastIncident ?? 'N/A'}
                  color="green"
                />
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                {/* By Severity */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Incidents by Severity</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {Object.entries(indicators?.lagging?.bySeverity || {}).map(([sev, count]) => {
                        const total = Object.values(indicators?.lagging?.bySeverity || {}).reduce((a, b) => a + b, 0);
                        const pct = total > 0 ? (count / total) * 100 : 0;
                        const colorMap = {
                          critical: 'bg-red-500',
                          high: 'bg-orange-500',
                          medium: 'bg-yellow-500',
                          low: 'bg-green-500'
                        };
                        const color = colorMap[sev.toLowerCase()] || 'bg-gray-500';
                        
                        return (
                          <div key={sev} className="flex items-center gap-3">
                            <span className="text-sm text-gray-600 w-20 capitalize">{sev}</span>
                            <div className="flex-1 bg-gray-100 rounded-full h-3">
                              <div className={`${color} h-3 rounded-full`} style={{ width: `${Math.max(pct, 3)}%` }} />
                            </div>
                            <span className="font-medium w-8 text-right">{count}</span>
                          </div>
                        );
                      })}
                      
                      {Object.keys(indicators?.lagging?.bySeverity || {}).length === 0 && (
                        <p className="text-sm text-gray-400 italic text-center py-4">No incidents recorded</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
                
                {/* By Type */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Incidents by Type</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {Object.entries(indicators?.lagging?.byType || {}).slice(0, 6).map(([type, count]) => {
                        const total = Object.values(indicators?.lagging?.byType || {}).reduce((a, b) => a + b, 0);
                        const pct = total > 0 ? (count / total) * 100 : 0;
                        
                        return (
                          <div key={type} className="flex items-center gap-3">
                            <span className="text-sm text-gray-600 w-24 truncate" title={type}>{type}</span>
                            <div className="flex-1 bg-gray-100 rounded-full h-3">
                              <div className="bg-blue-500 h-3 rounded-full" style={{ width: `${Math.max(pct, 3)}%` }} />
                            </div>
                            <span className="font-medium w-8 text-right">{count}</span>
                          </div>
                        );
                      })}
                      
                      {Object.keys(indicators?.lagging?.byType || {}).length === 0 && (
                        <p className="text-sm text-gray-400 italic text-center py-4">No incidents recorded</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </section>
        
        {/* LAYER 4: Subcontractor Accountability */}
        <section>
          <div className="flex items-center gap-2 mb-4">
            <Building2 className="w-5 h-5 text-orange-600" />
            <h2 className="text-lg font-semibold text-gray-900">Subcontractor Accountability</h2>
          </div>
          
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Rankings */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Performance Rankings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Best Performers */}
                <div>
                  <p className="text-xs font-medium text-green-600 uppercase mb-2 flex items-center gap-1">
                    <Award className="w-3 h-3" /> Best Performers
                  </p>
                  {subcontractorScorecard?.rankings?.bestPerformers?.length > 0 ? (
                    <div className="space-y-1">
                      {subcontractorScorecard.rankings.bestPerformers.map((sub, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-green-50 rounded">
                          <span className="text-sm">{sub.name}</span>
                          <span className="font-bold text-green-600">{sub.score}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400 italic">None</p>
                  )}
                </div>
                
                {/* At Risk */}
                <div>
                  <p className="text-xs font-medium text-orange-600 uppercase mb-2 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" /> At Risk
                  </p>
                  {subcontractorScorecard?.rankings?.atRisk?.length > 0 ? (
                    <div className="space-y-1">
                      {subcontractorScorecard.rankings.atRisk.map((sub, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-orange-50 rounded">
                          <span className="text-sm">{sub.name}</span>
                          <RiskBadge level={sub.status} />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400 italic">None</p>
                  )}
                </div>
              </CardContent>
            </Card>
            
            {/* Trend Alerts */}
            <Card className="lg:col-span-2">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Trend Alerts</CardTitle>
                <CardDescription>Automatic flags for safety concerns</CardDescription>
              </CardHeader>
              <CardContent>
                {subcontractorScorecard?.alerts?.length > 0 ? (
                  <div className="grid md:grid-cols-2 gap-3">
                    {subcontractorScorecard.alerts.map((alert, idx) => (
                      <AlertCard key={idx} alert={alert} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <CheckCircle className="w-12 h-12 text-green-300 mx-auto mb-2" />
                    <p className="text-gray-500">No alerts at this time</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
          
          {/* Full Scorecards */}
          {subcontractorScorecard?.scorecards?.length > 0 && (
            <div className="mt-6">
              <p className="text-sm font-medium text-gray-700 mb-3">Detailed Scorecards</p>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {subcontractorScorecard.scorecards.map(card => (
                  <Card key={card.subCompanyId} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <p className="font-semibold text-gray-900">{card.subCompanyName}</p>
                          {card.divisionName && (
                            <p className="text-xs text-gray-500">{card.divisionName}</p>
                          )}
                        </div>
                        <SafetyScoreRing 
                          score={card.safetyHealthScore.score}
                          status={card.safetyHealthScore.status}
                          color={card.safetyHealthScore.color}
                          size="small"
                        />
                      </div>
                      
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-500">JSA:</span>
                          <span className="font-medium">{card.metrics.jsaComplianceRate}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Audit Avg:</span>
                          <span className="font-medium">{card.metrics.avgAuditScore}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Incidents:</span>
                          <span className="font-medium">{card.counts.incidents}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Trend:</span>
                          <TrendIndicator direction={card.trend.direction} value={card.trend.changePercent} />
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between mt-3 pt-3 border-t text-xs text-gray-500">
                        <span>{card.counts.workers} workers</span>
                        <span>{card.counts.jsas} JSAs</span>
                        <span>{card.counts.audits} audits</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </section>
        
        {/* LAYER 5: Executive KPIs */}
        <section>
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900">Executive KPIs</h2>
          </div>
          
          <Card className="bg-gradient-to-r from-slate-900 to-slate-800 text-white">
            <CardContent className="p-6">
              <div className="grid md:grid-cols-6 gap-6">
                <div className="text-center">
                  <p className="text-4xl font-bold">{executiveKpis?.gcWide?.totalActiveProjects || 0}</p>
                  <p className="text-sm text-gray-300">Active Projects</p>
                </div>
                <div className="text-center">
                  <p className="text-4xl font-bold">{executiveKpis?.currentProject?.totalWorkersOnSite || 0}</p>
                  <p className="text-sm text-gray-300">Workers on Site</p>
                </div>
                <div className="text-center">
                  <p className="text-4xl font-bold">{executiveKpis?.currentProject?.totalSubcontractors || 0}</p>
                  <p className="text-sm text-gray-300">Subcontractors</p>
                </div>
                <div className="text-center">
                  <p className="text-4xl font-bold">{executiveKpis?.currentProject?.avgSafetyScore || 0}%</p>
                  <p className="text-sm text-gray-300">Avg Safety Score</p>
                </div>
                <div className="text-center">
                  <p className="text-4xl font-bold">{executiveKpis?.currentProject?.jobsBlockedPercent || 0}%</p>
                  <p className="text-sm text-gray-300">Jobs Blocked</p>
                </div>
                <div className="text-center">
                  <p className="text-4xl font-bold">{executiveKpis?.currentProject?.timeSavedHours || 0}h</p>
                  <p className="text-sm text-gray-300">Time Saved</p>
                </div>
              </div>
              
              <div className="mt-6 pt-6 border-t border-gray-700 flex items-center justify-between">
                <div>
                  <Badge className={
                    executiveKpis?.highlights?.safetyReadiness === 'Good' ? 'bg-green-500' :
                    executiveKpis?.highlights?.safetyReadiness === 'Needs Attention' ? 'bg-yellow-500' :
                    'bg-red-500'
                  }>
                    Safety Readiness: {executiveKpis?.highlights?.safetyReadiness || 'N/A'}
                  </Badge>
                </div>
                <div className="text-sm text-gray-400">
                  {executiveKpis?.highlights?.totalJsasCreated || 0} JSAs • {executiveKpis?.highlights?.totalAuditsCompleted || 0} Audits
                </div>
              </div>
            </CardContent>
          </Card>
        </section>
        
        {/* AI INSIGHTS Section */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Brain className="w-5 h-5 text-violet-600" />
              <h2 className="text-lg font-semibold text-gray-900">AI-Powered Insights</h2>
              <Badge variant="outline" className="text-violet-600 border-violet-300">GPT-5.1</Badge>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={fetchAiInsights}
              disabled={aiLoading}
            >
              {aiLoading ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Zap className="w-4 h-4 mr-2" />
                  Generate Insights
                </>
              )}
            </Button>
          </div>
          
          <Card className="bg-gradient-to-br from-violet-50 to-purple-50 border-violet-200">
            <CardContent className="p-6">
              {aiInsights?.insights?.length > 0 ? (
                <div className="space-y-4">
                  {aiInsights.insights.map((insight, idx) => (
                    <div key={idx} className="p-4 bg-white rounded-lg shadow-sm border border-violet-100">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0">
                          <Lightbulb className="w-4 h-4 text-violet-600" />
                        </div>
                        <div className="prose prose-sm max-w-none">
                          <p className="text-gray-700 whitespace-pre-wrap">{insight}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  <p className="text-xs text-gray-500 text-right">
                    Analyzed: {aiInsights.dataAnalyzed?.nearMisses || 0} near-misses, {aiInsights.dataAnalyzed?.incidents || 0} incidents, {aiInsights.dataAnalyzed?.jsasReviewed || 0} JSAs
                  </p>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Brain className="w-12 h-12 text-violet-300 mx-auto mb-3" />
                  <p className="text-gray-600 mb-2">AI-powered safety insights</p>
                  <p className="text-sm text-gray-500">Click &quot;Generate Insights&quot; to analyze patterns in your safety data</p>
                </div>
              )}
            </CardContent>
          </Card>
        </section>
      </div>
    </div>
  );
};

export default GCAnalyticsDashboard;
